import React, { FunctionComponent, useEffect } from "react";
import styles from "./Events.module.scss";
import EventsProps from "./EventsProps.interface";

import fetchEvents from "./hooks/fetchEvents";
import Event from "./components/Event/Event";

import { motion, useAnimation } from "framer-motion";
import { useHistory } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import Dictionary from "components/Dictionary/Dictionary";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import isBrowserContext from "helpers/isBrowserContext";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";

const Events: FunctionComponent<EventsProps> = (props) => {
    const history = useHistory();

    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const { filteredItems, viewState, currentFilter, setCurrentFilter } = fetchEvents();
    
    const currentFilterUpcomingClass = currentFilter === "UPCOMING" ? styles.is_active : undefined;
    const currentFilterEndedClass = currentFilter === "ENDED" ? styles.is_active : undefined;

    const onFilterClick = (name: string) => {
        if (name) {
            // Setting current filter
            setCurrentFilter(name);

            // Setting URL Params
            history.replace({
                pathname: location.pathname,
                search: `?b=blockEventsList&f=${name}`,
                hash: location.hash
            });
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(history.location.search);
        const filter = query.get("f");

        if (typeof (filter) !== "undefined" && filter !== null) {
            setCurrentFilter(filter);
        }
    }, []);

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.2, });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={styles.container}>
                {/* Filters */}
                <div className={styles.tags}>
                    <button tabIndex={1} onClick={() => onFilterClick("UPCOMING")} className={combineClasses([styles.tag, currentFilterUpcomingClass])}>
                        <Dictionary name="EVENTS_LIST.UPCOMING_FILTER" />
                    </button>
                    <button tabIndex={2} onClick={() => onFilterClick("ENDED")} className={combineClasses([styles.tag, currentFilterEndedClass])}>
                        <Dictionary name="EVENTS_LIST.ENDED_FILTER" />
                    </button>
                </div>

                {viewState === "LOADING" ? (
                    <div>
                        <div className={styles.loader}>Loading...</div>
                    </div>
                ) : null}

                {viewState === "LOADED" ? (
                    <div className={styles.list}>
                        {filteredItems.map((item: any) => {
                            return (
                                <Event key={item._id} {...item} />
                            );
                        })}
                    </div>
                ) : null}

                {viewState === "EMPTY" ? (
                    <div className={styles.empty}>
                        <Dictionary name="EVENTS_LIST.EMPTY" />
                    </div>
                ) : null}

                {viewState === "ERROR" ? (
                    <div className={styles.error}>
                        <Dictionary name="EVENTS_LIST.ERROR" />
                    </div>
                ) : null}
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default Events;